import { metricStudioLocation } from '../../../../router/tenantadmin/metric_studio_routes.js';

export const API = '/api/hermes';

export function dataAttributeLocation(id) {
  return metricStudioLocation({ artifact: 'attribute', id });
}

export const TABS = [
  {
    key: 'overview', label: 'Overview', route: 'AdminHermesOverview',
    description: 'A health check for Hermes on this tenant. See runs that failed or are taking too long, changes waiting for approval, and what Hermes has cost over the last 30 days.',
  },
  {
    key: 'runs', label: 'Runs', route: 'AdminHermesRuns',
    description: 'A log of every time a template has run, whether a person, a schedule or an event started it. Open a run to see its progress, results, errors and cost, or start a new one.',
  },
  {
    key: 'templates', label: 'Templates', route: 'AdminHermesTemplates',
    description: 'The analyses Hermes knows how to do. Each template sets who it covers, the data the AI reads, the instructions it follows, and what it is allowed to write back.',
  },
  {
    key: 'executors', label: 'Executors', route: 'AdminHermesExecutors',
    description: 'The AI setups that run templates: which model, and how it works through the data. Choose which ones are on for this tenant and what each may change: nothing, only with approval, or directly.',
  },
  {
    key: 'approvals', label: 'Approvals', route: 'AdminHermesApprovals',
    description: 'Changes a run wants to make to your data that need a person to sign off. Approve to write the change now, or decline to discard it. Approved changes cannot be undone from here.',
  },
  {
    key: 'schedules', label: 'Schedules', route: 'AdminHermesSchedules',
    description: 'Templates set to run on their own, daily, weekly or monthly. Each schedule pairs a template with an executor and shows what it may change while nobody is watching.',
  },
  {
    key: 'tests', label: 'Tests', route: 'AdminHermesTests', superuserOnly: true,
    description: 'Kairon staff only. Scripted conversations that grade the chat agent against known right answers, so model and prompt changes can be compared before they ship.',
  },
  {
    key: 'console', label: 'Console', route: 'AdminHermesConsole', superuserOnly: true,
    description: 'Kairon staff only. Live view of chat agent conversations across every tenant: who asked what, what it cost, and which answers failed or were stopped.',
  },
];

export const STATUS_STYLE = {
  completed: 'bg-secondary-200 text-gray-900',
  running: 'bg-neutral-100 text-gray-900',
  pending: 'bg-gray-100 text-gray-800',
  failed: 'bg-red-tint text-gray-900',
  cancelled: 'bg-gray-100 text-gray-800',
  finished: 'bg-secondary-200 text-gray-900',
  executing: 'bg-neutral-100 text-gray-900',
  declined: 'bg-gray-100 text-gray-800',
  direct: 'bg-primary-100 text-gray-900',
  propose: 'bg-neutral-100 text-gray-900',
  none: 'bg-gray-100 text-gray-700',
  active: 'bg-secondary-200 text-gray-900',
  inactive: 'bg-gray-100 text-gray-700',
};

export const PRIMARY_BUTTON = 'inline-flex items-center shrink-0 whitespace-nowrap px-3 py-2 rounded-md font-medium bg-primary-500 text-gray-900 hover:bg-primary-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed';
export const SECONDARY_BUTTON = 'inline-flex items-center shrink-0 whitespace-nowrap px-3 py-2 rounded-md font-medium border border-neutral-700 text-neutral-800 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed';
export const TEXT_BUTTON =' font-medium text-neutral-800 hover:text-neutral-1000 disabled:opacity-50 disabled:cursor-not-allowed';
export const INPUT = 'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-primary-500 focus:outline-none';
export const SIZED_INPUT = INPUT.replace(' w-full', '');
export const TH = 'px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500';
export const TD = 'px-3 py-3 align-top text-gray-900';

export function templateOptions(templates) {
  return templates.map((t) => ({ value: String(t.id), label: t.name }));
}

export function money(value) {
  const n = Number(value || 0);
  if (n > 0 && n < 0.01) return '<$0.01';
  return `$${n.toFixed(2)}`;
}

export function duration(start, end) {
  if (!start) return '';
  const seconds = Math.max(0, Math.round(((end ? new Date(end) : new Date()) - new Date(start)) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function seconds(n) {
  return duration(new Date(0), new Date(Number(n || 0) * 1000));
}

export function relative(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso)) / 1000;
  const future = diff < 0;
  const s = Math.abs(diff);
  let text;
  if (s < 60) text = 'just now';
  else if (s < 3600) text = `${Math.round(s / 60)}m`;
  else if (s < 86400) text = `${Math.round(s / 3600)}h`;
  else text = `${Math.round(s / 86400)}d`;
  if (text === 'just now') return text;
  return future ? `in ${text}` : `${text} ago`;
}

export function absolute(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function words(value) {
  return String(value ?? '').replace(/_/g, ' ');
}

export function tokens(n) {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${Math.round(v / 1e3)}k`;
  return String(v);
}

export function progressLine(run) {
  const p = run.progress || {};
  if (p.units !== undefined) {
    const failed = p.failed ? ` · ${p.failed} failed` : '';
    return `${p.done || 0}/${p.units} units${failed}`;
  }
  if (p.turns !== undefined) return `${p.turns} turns · ${p.results || 0} results`;
  return '';
}

export function errorOf(e, fallback) {
  return e?.response?.data?.error || e?.response?.data?.detail || fallback;
}

export function schemaFields(schema) {
  const props = (schema && schema.properties) || {};
  const required = new Set((schema && schema.required) || []);
  return Object.entries(props).map(([name, spec]) => ({ name, spec: spec || {}, required: required.has(name) }));
}

export function cellValue(value) {
  if (value === undefined || value === null) return null;
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
  if (typeof value === 'object') return value.text !== undefined ? String(value.text) : null;
  if (typeof value === 'number') return String(value);
  return words(value);
}

export function isProse(name, spec, value) {
  if (['rationale', 'summary', 'explanation', 'narrative'].includes(name)) return true;
  if (value && typeof value === 'object' && value.text !== undefined) return true;
  return typeof value === 'string' && value.length > 80 && !(spec && spec.enum);
}

export function effectSummary(effect) {
  const p = effect.payload || {};
  const pairs = Object.entries(p).filter(([, v]) => v !== null && typeof v !== 'object').slice(0, 3);
  return pairs.map(([k, v]) => `${words(k)} ${v}`).join(' · ') || 'No payload';
}

export function providerFor(model) {
  const name = String(model || '').toLowerCase();
  if (name.startsWith('vertex/claude')) return 'Claude on Vertex';
  if (name.startsWith('gpt') || name.startsWith('o3') || name.startsWith('o4') || name.startsWith('openai/')) return 'OpenAI';
  if (name.startsWith('gemini')) return 'Vertex';
  if (name.startsWith('claude') || name.startsWith('anthropic/')) return 'Anthropic';
  return '';
}

export const RUNTIME_MEANING = {
  single_call: 'One structured call per unit, fanned out over the population.',
  tool_loop: 'Agent loop that queries the inputs as tables over many turns.',
  bench: 'Plays each case through a real interactive session as the requester, scores it, and grades the reply with this model as judge.',
};
